"use client";

import { IconMinusOutline18, IconPlusOutline18 } from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RRule, Weekday, rrulestr } from "rrule";

// --- Types ---

type FreqUnit = "seconds" | "minutes" | "hours" | "days" | "weeks" | "months";
type EndCondition = "never" | "on" | "after";

interface SimpleFields {
  freq: FreqUnit;
  interval: number;
  atHour: number;
  atMinute: number;
  weekdays: number[]; // 0=MO..6=SU (rrule weekday index)
  monthDays: number[]; // 1-31
  endCondition: EndCondition;
  until: string; // ISO date string YYYY-MM-DD
  count: number;
  startDate: Date | null;
}

interface RecurrenceInputProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

// --- Constants ---

const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const WEEKDAY_FULL_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// rrule weekday objects in order Mo-Su
const RRULE_WEEKDAYS = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];

const FREQ_OPTIONS: { value: FreqUnit; label: string }[] = [
  { value: "seconds", label: "seconds" },
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
];

const FREQ_MAP: Record<FreqUnit, number> = {
  seconds: RRule.SECONDLY,
  minutes: RRule.MINUTELY,
  hours: RRule.HOURLY,
  days: RRule.DAILY,
  weeks: RRule.WEEKLY,
  months: RRule.MONTHLY,
};

const REVERSE_FREQ_MAP: Record<number, FreqUnit> = {
  [RRule.SECONDLY]: "seconds",
  [RRule.MINUTELY]: "minutes",
  [RRule.HOURLY]: "hours",
  [RRule.DAILY]: "days",
  [RRule.WEEKLY]: "weeks",
  [RRule.MONTHLY]: "months",
};

const DEFAULT_FIELDS: SimpleFields = {
  freq: "weeks",
  interval: 1,
  atHour: 9,
  atMinute: 0,
  weekdays: [0], // Monday
  monthDays: [1],
  endCondition: "never",
  until: "",
  count: 10,
  startDate: null,
};

// --- Utilities ---

function padTime(n: number): string {
  return n.toString().padStart(2, "0");
}

function freqNeedsTimePicker(freq: FreqUnit): boolean {
  return freq === "days" || freq === "weeks" || freq === "months";
}

function getTimezoneAbbr(): string {
  return (
    new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(new Date())
      .find(p => p.type === "timeZoneName")?.value ?? "local"
  );
}

function formatTime12h(hour: number, minute: number): string {
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return minute === 0 ? `${h12} ${ampm}` : `${h12}:${minute.toString().padStart(2, "0")} ${ampm}`;
}

/**
 * Calculate how many days the UTC date is ahead of (or behind) the local date
 * for a given local Date. Returns -1, 0, or +1.
 *
 * Example: 11 PM EST = next day in UTC → returns +1
 * Example: 2 AM UTC+5 = previous day in UTC → returns -1
 * Example: noon EST = same day in UTC → returns 0
 */
function getUtcDayShift(date: Date): number {
  let shift = date.getUTCDay() - date.getDay();
  if (shift > 1) shift -= 7; // e.g. Sat(6) - Sun(0) = 6 → -1
  if (shift < -1) shift += 7; // e.g. Sun(0) - Sat(6) = -6 → +1
  return shift;
}

function buildRRule(fields: SimpleFields): string {
  const options: Partial<ConstructorParameters<typeof RRule>[0]> = {
    freq: FREQ_MAP[fields.freq],
    interval: fields.interval,
  };

  // atHour/atMinute are in the user's local timezone.
  // Construct a local Date so rrule.js serialises the correct UTC in DTSTART.
  const now = new Date();
  if (fields.freq === "seconds" || fields.freq === "minutes") {
    // Sub-hourly: DTSTART = now so the first occurrence fires immediately.
    // The time picker is not shown for these frequencies, and using a stale
    // atHour/atMinute (from defaults or a previous schedule) would set a
    // future DTSTART, preventing the cron from firing until that time.
    options.dtstart = now;
  } else if (fields.freq === "hours") {
    // Hourly: DTSTART = today at 00:atMinute so the :MM alignment is preserved
    // and the first occurrence is always in the past (or at most ~59 min away).
    options.dtstart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, fields.atMinute, 0);
  } else {
    // Daily/weekly/monthly: use user-selected time (time picker is visible).
    options.dtstart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), fields.atHour, fields.atMinute, 0);
  }

  // When the local time crosses the UTC midnight boundary, the UTC day differs
  // from the local day. BYDAY/BYMONTHDAY in rrule operate in UTC, so we must
  // shift weekdays/month-days to match the UTC calendar.
  const utcDayShift = getUtcDayShift(options.dtstart);

  if (fields.freq === "weeks" && fields.weekdays.length > 0) {
    options.byweekday = fields.weekdays.map(i => {
      const shifted = (i + utcDayShift + 7) % 7;
      return RRULE_WEEKDAYS[shifted];
    });
  }

  if (fields.freq === "months" && fields.monthDays.length > 0) {
    if (utcDayShift !== 0) {
      options.bymonthday = fields.monthDays.map(d => {
        const shifted = d + utcDayShift;
        // Clamp to 1-28 to avoid month-boundary edge cases
        return Math.max(1, Math.min(28, shifted));
      });
    } else {
      options.bymonthday = fields.monthDays;
    }
  }

  if (fields.freq === "hours") {
    options.byminute = [fields.atMinute];
  }

  if (fields.endCondition === "on" && fields.until) {
    const [y, m, d] = fields.until.split("-").map(Number);
    options.until = new Date(Date.UTC(y, m - 1, d, 23, 59, 59));
  } else if (fields.endCondition === "after" && fields.count > 0) {
    options.count = fields.count;
  }

  const rule = new RRule(options);
  return rule.toString();
}

function parseRRuleToFields(rruleString: string): { success: true; fields: SimpleFields } | { success: false } {
  try {
    const rule = rrulestr(rruleString);
    const opts = rule.options;

    const freq = REVERSE_FREQ_MAP[opts.freq];
    if (!freq) return { success: false };

    let endCondition: EndCondition = "never";
    let until = "";
    let count = 10;

    if (opts.until) {
      endCondition = "on";
      const d = opts.until;
      until = `${d.getUTCFullYear()}-${padTime(d.getUTCMonth() + 1)}-${padTime(d.getUTCDate())}`;
    } else if (opts.count) {
      endCondition = "after";
      count = opts.count;
    }

    // Reverse the UTC day shift applied during buildRRule so the UI shows
    // local weekdays/month-days, not UTC ones.
    const utcDayShift = opts.dtstart ? getUtcDayShift(opts.dtstart) : 0;

    let weekdays = DEFAULT_FIELDS.weekdays;
    if (opts.byweekday && opts.byweekday.length > 0) {
      weekdays = opts.byweekday.map((wd: number | Weekday) => {
        const wdNum = typeof wd === "number" ? wd : wd.weekday;
        return (wdNum - utcDayShift + 7) % 7;
      });
    }

    let monthDays = DEFAULT_FIELDS.monthDays;
    if (opts.bymonthday && opts.bymonthday.length > 0) {
      if (utcDayShift !== 0) {
        monthDays = opts.bymonthday.map((d: number) => Math.max(1, Math.min(28, d - utcDayShift)));
      } else {
        monthDays = opts.bymonthday;
      }
    }

    let atHour = DEFAULT_FIELDS.atHour;
    let atMinute = DEFAULT_FIELDS.atMinute;

    // Extract display time from DTSTART using local getters (getHours/getMinutes).
    // IMPORTANT: Do NOT use opts.byhour/opts.byminute — rrule.js internally
    // defaults those from DTSTART's UTC components (getUTCHours), which causes
    // the displayed time to shift by the user's UTC offset on every reload.
    const startDate = opts.dtstart ?? null;
    if (opts.dtstart) {
      atHour = opts.dtstart.getHours();
      atMinute = opts.dtstart.getMinutes();
    }

    return {
      success: true,
      fields: {
        freq,
        interval: opts.interval || 1,
        atHour,
        atMinute,
        weekdays,
        monthDays,
        endCondition,
        until,
        count,
        startDate,
      },
    };
  } catch {
    return { success: false };
  }
}

/**
 * Build a human-readable description directly from the UI fields.
 * This avoids rrule.toText() which would show UTC weekday/month-day names
 * that differ from what the user selected when the UTC day shift is non-zero.
 */
function getFieldsDescription(fields: SimpleFields): { text: string; valid: boolean } {
  const { freq, interval, atHour, atMinute, weekdays, monthDays, endCondition, count } = fields;

  const singular: Record<FreqUnit, string> = {
    seconds: "second",
    minutes: "minute",
    hours: "hour",
    days: "day",
    weeks: "week",
    months: "month",
  };

  let text = interval === 1 ? `Every ${singular[freq]}` : `Every ${interval} ${freq}`;

  if (freq === "weeks" && weekdays.length > 0) {
    const names = [...weekdays].sort((a, b) => a - b).map(i => WEEKDAY_FULL_NAMES[i]);
    text += ` on ${names.join(", ")}`;
  }

  if (freq === "months" && monthDays.length > 0) {
    const days = [...monthDays]
      .sort((a, b) => a - b)
      .map(d => `${d}${getOrdinalSuffix(d)}`)
      .join(", ");
    text += ` on the ${days}`;
  }

  if (freqNeedsTimePicker(freq)) {
    text += ` at ${formatTime12h(atHour, atMinute)} ${getTimezoneAbbr()}`;
  } else if (freq === "hours") {
    text += ` at :${padTime(atMinute)} of each hour`;
  }

  if (endCondition === "after" && count > 0) {
    text += `, ${count} times`;
  }

  return { text, valid: true };
}

// --- Sub-components ---

function Stepper({
  value,
  onChange,
  min,
  max,
  suffix,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  suffix?: string;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState(value.toString());
  const canDecrement = value > min;
  const canIncrement = value < max;

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  return (
    <div className={`inline-flex flex-col ${disabled ? "opacity-40" : ""}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => canDecrement && onChange(value - 1)}
          disabled={disabled || !canDecrement}
          className="border-ds-neutral-300 text-ds-text-subtle dark:border-ds-neutral-700 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 bg-ds-surface-card hover:bg-ds-surface-hover flex h-9 w-9 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Decrease"
        >
          <IconMinusOutline18 className="h-3 w-3" />
        </button>
        <Input
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={inputValue}
          onChange={e => {
            const raw = e.target.value;
            if (raw === "") {
              setInputValue("");
              return;
            }
            if (!/^\d+$/.test(raw)) return;
            setInputValue(raw);
            const n = parseInt(raw, 10);
            if (n >= min && n <= max) onChange(n);
          }}
          onBlur={() => {
            setInputValue(value.toString());
          }}
          className="text-ds-text-primary h-9 w-16 px-2 text-center"
        />
        <button
          type="button"
          onClick={() => canIncrement && onChange(value + 1)}
          disabled={disabled || !canIncrement}
          className="border-ds-neutral-300 text-ds-text-subtle dark:border-ds-neutral-700 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 bg-ds-surface-card hover:bg-ds-surface-hover flex h-9 w-9 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Increase"
        >
          <IconPlusOutline18 className="h-3 w-3" />
        </button>
      </div>
      {suffix && <span className="text-ds-text-muted mt-0.5 self-end text-xs">{suffix}</span>}
    </div>
  );
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const ampm = i < 12 ? "AM" : "PM";
  const h12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
  return { value: i, label: `${h12}:00 ${ampm}`, short: `${h12} ${ampm}` };
});

const COMMON_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const ALL_MINUTES = Array.from({ length: 60 }, (_, i) => i);

function TimePicker({
  hour,
  minute,
  onHourChange,
  onMinuteChange,
}: {
  hour: number;
  minute: number;
  onHourChange: (h: number) => void;
  onMinuteChange: (m: number) => void;
}) {
  const [showAllMinutes, setShowAllMinutes] = useState(!COMMON_MINUTES.includes(minute));
  const minuteOptions = showAllMinutes ? ALL_MINUTES : COMMON_MINUTES;

  return (
    <div className="space-y-1.5">
      <label className="label-xs-caps block">At ({getTimezoneAbbr()})</label>
      <div className="flex items-center gap-2">
        <Select value={hour.toString()} onValueChange={v => onHourChange(parseInt(v))}>
          <SelectTrigger className="h-9 w-[100px] text-sm">
            <SelectValue>{HOUR_OPTIONS[hour].short}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {HOUR_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value.toString()} className="text-sm">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-ds-text-muted text-sm">:</span>
        <Select
          value={minute.toString()}
          onValueChange={v => {
            if (v === "__show_all__") {
              setShowAllMinutes(true);
              return;
            }
            onMinuteChange(parseInt(v));
          }}
        >
          <SelectTrigger className="h-9 w-[72px] text-sm">
            <SelectValue>{padTime(minute)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {minuteOptions.map(m => (
              <SelectItem key={m} value={m.toString()} className="text-sm">
                :{padTime(m)}
              </SelectItem>
            ))}
            {!showAllMinutes && (
              <SelectItem value="__show_all__" className="caption-xs">
                All minutes...
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function WeekdayPicker({ selected, onChange }: { selected: number[]; onChange: (days: number[]) => void }) {
  const toggle = (day: number) => {
    if (selected.includes(day)) {
      if (selected.length <= 1) return;
      onChange(selected.filter(d => d !== day));
    } else {
      onChange([...selected, day]);
    }
  };

  return (
    <div className="space-y-1.5">
      <label className="label-xs-caps block">Repeat on</label>
      <div className="flex gap-1">
        {WEEKDAY_LABELS.map((label, i) => (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            aria-pressed={selected.includes(i)}
            aria-label={WEEKDAY_FULL_NAMES[i]}
            className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium transition-colors ${
              selected.includes(i)
                ? "bg-blue-600 text-white"
                : "border-ds-border-default bg-ds-neutral-100 text-ds-text-subtle hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 border"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function getOrdinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const MONTH_DAY_PRESETS: { label: string; days: number[] }[] = [
  { label: "1st", days: [1] },
  { label: "15th", days: [15] },
  { label: "1st & 15th", days: [1, 15] },
  { label: "31st", days: [31] },
];

function MonthDayPicker({ selected, onChange }: { selected: number[]; onChange: (days: number[]) => void }) {
  const [showGrid, setShowGrid] = useState(false);

  const toggle = (day: number) => {
    if (selected.includes(day)) {
      if (selected.length <= 1) return;
      onChange(selected.filter(d => d !== day));
    } else {
      onChange([...selected, day]);
    }
  };

  const matchesPreset = (preset: number[]) =>
    preset.length === selected.length &&
    preset.every(d => selected.includes(d)) &&
    selected.every(d => preset.includes(d));

  const selectedLabel =
    selected.length === 1
      ? `${selected[0]}${getOrdinalSuffix(selected[0])}`
      : selected.length <= 3
        ? [...selected]
            .sort((a, b) => a - b)
            .map(d => `${d}${getOrdinalSuffix(d)}`)
            .join(", ")
        : `${selected.length} days selected`;

  return (
    <div className="space-y-2">
      <label className="label-xs-caps block">Day of month</label>

      <div className="flex flex-wrap gap-1.5">
        {MONTH_DAY_PRESETS.map(preset => (
          <button
            key={preset.label}
            type="button"
            onClick={() => onChange(preset.days)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              matchesPreset(preset.days)
                ? "bg-blue-600 text-white"
                : "bg-ds-neutral-100 text-ds-text-subtle hover:bg-ds-neutral-200 dark:bg-ds-neutral-800 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setShowGrid(!showGrid)}
        className="border-ds-border-default flex w-full items-center justify-between rounded-lg border bg-zinc-50 px-3 py-2 text-left text-xs dark:bg-zinc-800/50"
      >
        <span className="text-ds-text-secondary">{selectedLabel}</span>
        <span className="text-ds-text-disabled dark:text-ds-text-muted">
          {showGrid ? "Hide" : "Pick specific days"}
        </span>
      </button>

      {showGrid && (
        <div className="border-ds-border-default rounded-lg border bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <div className="grid grid-cols-7 gap-0.5">
            {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
              <button
                key={day}
                type="button"
                onClick={() => toggle(day)}
                aria-pressed={selected.includes(day)}
                aria-label={`Day ${day}`}
                className={`text-2xs flex h-7 w-full items-center justify-center rounded font-medium transition-colors ${
                  selected.includes(day)
                    ? "bg-blue-600 text-white"
                    : "text-ds-text-subtle hover:bg-ds-neutral-200 dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700"
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EndConditionPicker({
  endCondition,
  until,
  count,
  onEndConditionChange,
  onUntilChange,
  onCountChange,
}: {
  endCondition: EndCondition;
  until: string;
  count: number;
  onEndConditionChange: (v: EndCondition) => void;
  onUntilChange: (v: string) => void;
  onCountChange: (v: number) => void;
}) {
  return (
    <div className="border-ds-neutral-200 border-t pt-3 dark:border-zinc-700/60">
      <label className="label-xs-caps mb-2 block">Ends</label>

      <RadioGroup value={endCondition} onValueChange={v => onEndConditionChange(v as EndCondition)} className="gap-2.5">
        {/* Never */}
        <div className="flex items-center gap-2.5">
          <RadioGroupItem value="never" id="end-never" />
          <label htmlFor="end-never" className="text-ds-text-secondary cursor-pointer text-sm">
            Never
          </label>
        </div>

        {/* On date */}
        <div className="flex items-center gap-2.5">
          <RadioGroupItem value="on" id="end-on" />
          <label htmlFor="end-on" className="text-ds-text-secondary cursor-pointer text-sm">
            On
          </label>
          <Input
            type="date"
            value={until}
            disabled={endCondition !== "on"}
            onChange={e => onUntilChange(e.target.value)}
            className="text-ds-text-primary h-8 w-auto px-2 transition-opacity"
          />
        </div>

        {/* After N occurrences */}
        <div className="flex items-start gap-2.5">
          <RadioGroupItem value="after" id="end-after" className="mt-2.5" />
          <label htmlFor="end-after" className="text-ds-text-secondary mt-2 cursor-pointer text-sm">
            After
          </label>
          <Stepper
            value={count}
            onChange={onCountChange}
            min={1}
            max={999}
            suffix="occurrences"
            disabled={endCondition !== "after"}
          />
        </div>
      </RadioGroup>
    </div>
  );
}

// --- Main Component ---

function initFields(val: string): SimpleFields {
  if (!val) return DEFAULT_FIELDS;
  const parsed = parseRRuleToFields(val);
  return parsed.success ? parsed.fields : DEFAULT_FIELDS;
}

export default function RecurrenceInput({ label, description, value, onChange }: RecurrenceInputProps) {
  const initializedRef = useRef(false);
  const lastEmittedRef = useRef<string>("");

  const initialFields = useMemo(() => initFields(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [fields, setFields] = useState<SimpleFields>(initialFields);

  // On mount, if value is empty, emit a default
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    if (!value) {
      const defaultRRule = buildRRule(DEFAULT_FIELDS);
      lastEmittedRef.current = defaultRRule;
      onChange(defaultRRule);
    } else {
      lastEmittedRef.current = value || "";
    }
  }, [value, onChange]);

  // Sync with external value changes
  useEffect(() => {
    if (!initializedRef.current) return;
    if (value === lastEmittedRef.current) return;

    lastEmittedRef.current = value;
    setFields(initFields(value));
  }, [value]);

  const updateFromFields = useCallback(
    (newFields: SimpleFields) => {
      setFields(newFields);
      const rrule = buildRRule(newFields);
      lastEmittedRef.current = rrule;
      onChange(rrule);
    },
    [onChange],
  );

  // Get human-readable description from local fields (not from rrule string,
  // which would show UTC weekday names that differ from local after day shift).
  const { text: rruleDescription, valid: rruleValid } = getFieldsDescription(fields);

  return (
    <div className="space-y-2">
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      <div className="border-ds-neutral-200 space-y-3.5 rounded-xl border bg-zinc-50/50 p-3 dark:border-zinc-700/60 dark:bg-zinc-800/30">
        {/* Repeat every [N] [unit] */}
        <div className="space-y-1.5">
          <label className="label-xs-caps block">Repeat every</label>
          <div className="flex items-center gap-2">
            <Stepper
              value={fields.interval}
              onChange={v => updateFromFields({ ...fields, interval: v })}
              min={1}
              max={999}
            />
            <Select value={fields.freq} onValueChange={v => updateFromFields({ ...fields, freq: v as FreqUnit })}>
              <SelectTrigger className="h-9 w-[110px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} className="text-sm">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Weekday picker for weekly */}
        {fields.freq === "weeks" && (
          <WeekdayPicker
            selected={fields.weekdays}
            onChange={days => updateFromFields({ ...fields, weekdays: days })}
          />
        )}

        {/* Month day picker for monthly */}
        {fields.freq === "months" && (
          <MonthDayPicker
            selected={fields.monthDays}
            onChange={days => updateFromFields({ ...fields, monthDays: days })}
          />
        )}

        {/* Minute-of-hour stepper for hourly */}
        {fields.freq === "hours" && (
          <div className="space-y-1.5">
            <label className="label-xs-caps block">At minute</label>
            <Stepper
              value={fields.atMinute}
              onChange={v => updateFromFields({ ...fields, atMinute: v })}
              min={0}
              max={59}
              suffix="of each hour"
            />
          </div>
        )}

        {/* Time picker for daily/weekly/monthly */}
        {freqNeedsTimePicker(fields.freq) && (
          <TimePicker
            hour={fields.atHour}
            minute={fields.atMinute}
            onHourChange={h => updateFromFields({ ...fields, atHour: h })}
            onMinuteChange={m => updateFromFields({ ...fields, atMinute: m })}
          />
        )}

        {/* End condition */}
        <EndConditionPicker
          endCondition={fields.endCondition}
          until={fields.until}
          count={fields.count}
          onEndConditionChange={v => updateFromFields({ ...fields, endCondition: v })}
          onUntilChange={v => updateFromFields({ ...fields, until: v })}
          onCountChange={v => updateFromFields({ ...fields, count: v })}
        />
      </div>

      {/* Live preview */}
      {rruleDescription && (
        <p className={`text-xs italic ${rruleValid ? "text-ds-text-muted" : "text-red-500 dark:text-red-400"}`}>
          {rruleDescription}
        </p>
      )}
    </div>
  );
}
